import React from "react";
import { Box, Flex, Spinner, Text, VStack } from "@chakra-ui/react";
import { useColorModeValue } from "../components/ui/color-mode";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useRecentStocks } from "../hooks/useRecentStocks";
import { useStockInfo } from "../hooks/useStockInfo";
import StockSearch from "../components/StockSearch";

const RecentStockItem = ({ symbol, lastVisitedAt }) => {
  const { data: stockInfo, loading } = useStockInfo(symbol);
  const companyName = stockInfo?.company_name || symbol;
  const navigate = useNavigate();

  const formatDate = (dateString) => {
    if (!dateString) return "Unknown";
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  return (
    <Box
      as="button"
      onClick={() => navigate(`/stock/${symbol.toLowerCase()}`)}
      bg="rgba(255, 255, 255, 0.5)"
      _dark={{ bg: "rgba(0, 0, 0, 0.3)" }}
      borderWidth="1px"
      borderRadius="lg"
      p={4}
      textAlign="center"
      w="100%"
      maxW="300px"
      _hover={{
        bg: "rgba(255, 255, 255, 0.7)",
        _dark: { bg: "rgba(0, 0, 0, 0.5)" },
        transform: "translateY(-2px)",
        boxShadow: "md",
      }}
      transition="all 0.2s"
      cursor="pointer"
    >
      <VStack gap={2}>
        <Text fontSize="lg" fontWeight="bold">
          {symbol}
        </Text>
        <Text fontSize="sm" color="gray.600" _dark={{ color: "gray.300" }}>
          {loading ? "Loading..." : companyName}
        </Text>
        <Text fontSize="xs" color="gray.500" _dark={{ color: "gray.400" }}>
          Last opened: {formatDate(lastVisitedAt)}
        </Text>
      </VStack>
    </Box>
  );
};

const Browse = () => {
  const { isAuthenticated } = useAuth0();
  const { recents, loading: recentsLoading, error: recentsError, addRecent } = useRecentStocks();

  const handleSuggestionSelected = async (suggestion) => {
    if (suggestion?.entity_type === "pre_ipo" || !suggestion?.ticker) return;
    try {
      await addRecent?.(suggestion.ticker);
    } catch (err) {
      console.error("Failed to record recent stock", err);
    }
  };

  const bgStart = useColorModeValue("gray.50", "gray.800");
  const bgEnd = useColorModeValue("teal.200", "teal.900");
  const textColor = useColorModeValue("gray.800", "white");
  const mutedTextColor = useColorModeValue("gray.600", "gray.300");
  const searchBg = useColorModeValue("white", "gray.900");
  const borderColor = useColorModeValue("gray.200", "gray.700");

  return (
    <Flex
      minH="calc(100vh - var(--nav-h))"
      direction="column"
      justify="flex-start"
      bgGradient="to-b"
      gradientFrom={bgStart}
      gradientTo={bgEnd}
      color={textColor}
      px={{ base: 5, md: 8 }}
      py={{ base: 12, md: 16 }}
    >
      <VStack gap={6} w="100%" maxW="640px" mx="auto" align="stretch">
        <Box textAlign="center">
          <Text as="h1" fontSize={{ base: "3xl", md: "4xl" }} fontWeight="bold">
            Browse Stocks
          </Text>
          <Text mt={2} color={mutedTextColor} fontSize={{ base: "md", md: "lg" }}>
            Search public stocks by ticker or company name, or find pre-IPO SEC filers by
            legal name, CIK, or proposed ticker.
          </Text>
        </Box>

        <Box
          bg={searchBg}
          borderWidth="1px"
          borderColor={borderColor}
          borderRadius="md"
          boxShadow="md"
          p={{ base: 4, md: 5 }}
          position="relative"
        >
          <StockSearch
            onSuggestionSelected={handleSuggestionSelected}
            inputProps={{
              "aria-label": "Search by ticker, company name, or CIK",
            }}
          />
        </Box>
      </VStack>

      {isAuthenticated && (
        <Box
          bg="rgba(255, 255, 255, 0.5)"
          _dark={{ bg: "rgba(0, 0, 0, 0.3)" }}
          borderTopWidth="1px"
          py={6}
          px={6}
          mt={8}
          w="100%"
        >
          <VStack gap={6} maxW="1200px" mx="auto" align="center">
            <Text fontSize="xl" fontWeight="semibold" textAlign="center">
              Recently visited
            </Text>
            {recentsLoading && <Spinner size="sm" />}
            {recentsError && (
              <Text fontSize="sm" color="red.500" textAlign="center">
                {recentsError}
              </Text>
            )}
            {!recentsLoading && !recentsError && recents.length === 0 && (
              <Text fontSize="sm" color="gray.500" textAlign="center">
                Your recent stocks will appear here after you browse.
              </Text>
            )}
            {!recentsLoading && recents.length > 0 && (
              <Flex wrap="wrap" gap={4} justify="center">
                {recents.map((r) => (
                  <RecentStockItem
                    key={r.symbol}
                    symbol={r.symbol}
                    lastVisitedAt={r.last_visited_at}
                  />
                ))}
              </Flex>
            )}
          </VStack>
        </Box>
      )}
    </Flex>
  );
};

export default Browse;
